import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { Hinweis, Knopf } from '@/components/ui';
import { InstallierenKnopf } from '@/components/Installieren';
import { useAuth } from '@/features/auth/AuthKontext';
import { useDarstellung } from '@/lib/darkmode';
import { SyncAnzeige } from '@/offline/SyncAnzeige';

/**
 * Grundgerüst der App.
 *
 * Der Sync-Status steht dauerhaft in der Kopfzeile (SPEC Kap. 12) — wer vor
 * Ort arbeitet, muss jederzeit sehen, ob Daten noch warten.
 */

interface Eintrag {
  pfad: string;
  text: string;
  symbol: string;
  nurAdmin?: boolean;
}

const NAVIGATION: Eintrag[] = [
  { pfad: '/', text: 'Übersicht', symbol: '⌂' },
  { pfad: '/protokolle', text: 'Protokolle', symbol: '✎' },
  { pfad: '/objekte', text: 'Objekte', symbol: '⌂' },
  { pfad: '/termine', text: 'Termine', symbol: '📅' },
  { pfad: '/vorlagen', text: 'Vorlagen', symbol: '❏', nurAdmin: true },
  { pfad: '/import', text: 'Import', symbol: '↥', nurAdmin: true },
  { pfad: '/verwaltung', text: 'Verwaltung', symbol: '⚙', nurAdmin: true },
];

export const AppRahmen = () => {
  const { nutzer, abmelden, istAdmin } = useAuth();
  const { modus, setzeModus } = useDarstellung();
  const [menueOffen, setMenueOffen] = useState(false);

  const sichtbar = NAVIGATION.filter((eintrag) => !eintrag.nurAdmin || istAdmin);
  const ohneRolle = nutzer !== null && nutzer.gruppen.length === 0;
  const naechsterModus = modus === 'dunkel' ? 'hell' : modus === 'hell' ? 'system' : 'dunkel';

  return (
    <div className="flex min-h-full flex-col bg-slate-50 dunkel:bg-slate-950">
      <header className="nicht-drucken sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur dunkel:border-slate-700 dunkel:bg-slate-900/95">
        <div className="mx-auto flex min-h-14 max-w-7xl items-center gap-3 px-3 sm:px-4">
          <button
            type="button"
            className="min-h-touch min-w-touch rounded-lg text-xl text-slate-700 hover:bg-slate-100 lg:hidden dunkel:text-slate-200 dunkel:hover:bg-slate-800"
            aria-label={menueOffen ? 'Menü schließen' : 'Menü öffnen'}
            aria-expanded={menueOffen}
            onClick={() => setMenueOffen((offen) => !offen)}
          >
            {menueOffen ? '✕' : '☰'}
          </button>

          <NavLink
            to="/"
            className="flex items-center gap-2 font-semibold text-slate-900 dunkel:text-slate-50"
          >
            <span className="flex size-8 items-center justify-center rounded-lg bg-marke-600 text-white">
              ✓
            </span>
            <span className="hidden sm:inline">Übergabeprotokoll</span>
          </NavLink>

          <nav className="ml-4 hidden items-center gap-1 lg:flex" aria-label="Hauptnavigation">
            {sichtbar.map((eintrag) => (
              <NavLink
                key={eintrag.pfad}
                to={eintrag.pfad}
                end={eintrag.pfad === '/'}
                className={({ isActive }) =>
                  cn(
                    'flex min-h-touch items-center rounded-lg px-3 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-marke-50 text-marke-700 dunkel:bg-marke-900/40 dunkel:text-marke-200'
                      : 'text-slate-600 hover:bg-slate-100 dunkel:text-slate-300 dunkel:hover:bg-slate-800',
                  )
                }
              >
                {eintrag.text}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <InstallierenKnopf />
            <SyncAnzeige />
            <button
              type="button"
              onClick={() => setzeModus(naechsterModus)}
              className="min-h-touch min-w-touch rounded-lg text-lg text-slate-600 hover:bg-slate-100 dunkel:text-slate-300 dunkel:hover:bg-slate-800"
              aria-label={`Darstellung umschalten (aktuell: ${modus})`}
              title={`Darstellung: ${modus}`}
            >
              {modus === 'dunkel' ? '☾' : modus === 'hell' ? '☀' : '◐'}
            </button>
            <NavLink
              to="/profil"
              className="hidden rounded-lg px-2 py-1 text-right hover:bg-slate-100 sm:block dunkel:hover:bg-slate-800"
              title="Mein Profil"
            >
              <span className="block text-sm font-medium text-slate-800 dunkel:text-slate-100">
                {nutzer?.vorname || nutzer?.email}
              </span>
              <span className="block text-xs text-slate-500 dunkel:text-slate-400">
                {nutzer?.gruppen.join(', ') || 'Ohne Rolle'}
              </span>
            </NavLink>
            <Knopf aussehen="still" groesse="klein" onClick={() => void abmelden()}>
              Abmelden
            </Knopf>
          </div>
        </div>

        {menueOffen && (
          <nav
            className="border-t border-slate-200 px-3 pb-3 lg:hidden dunkel:border-slate-700"
            aria-label="Hauptnavigation"
          >
            {sichtbar.map((eintrag) => (
              <NavLink
                key={eintrag.pfad}
                to={eintrag.pfad}
                end={eintrag.pfad === '/'}
                onClick={() => setMenueOffen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex min-h-touch items-center gap-3 rounded-lg px-3 text-base font-medium',
                    isActive
                      ? 'bg-marke-50 text-marke-700 dunkel:bg-marke-900/40 dunkel:text-marke-200'
                      : 'text-slate-700 dunkel:text-slate-200',
                  )
                }
              >
                <span aria-hidden="true" className="w-5 text-center">
                  {eintrag.symbol}
                </span>
                {eintrag.text}
              </NavLink>
            ))}
            {/* Auf dem Telefon ist die Kopfzeile zu schmal für den Namen — das Profil steht deshalb hier. */}
            <NavLink
              to="/profil"
              onClick={() => setMenueOffen(false)}
              className={({ isActive }) =>
                cn(
                  'flex min-h-touch items-center gap-3 rounded-lg px-3 text-base font-medium',
                  isActive
                    ? 'bg-marke-50 text-marke-700 dunkel:bg-marke-900/40 dunkel:text-marke-200'
                    : 'text-slate-700 dunkel:text-slate-200',
                )
              }
            >
              <span aria-hidden="true" className="w-5 text-center">
                ☺
              </span>
              Mein Profil
            </NavLink>
          </nav>
        )}
      </header>

      <main className="sicherer-bereich mx-auto w-full max-w-7xl flex-1 px-3 py-4 sm:px-4 sm:py-6">
        {/*
         * Ohne Cognito-Gruppe lehnt das Backend jede Abfrage ab. Die Anmeldung
         * funktioniert trotzdem — ohne diesen Hinweis sieht das aus wie eine
         * leere App mit hängendem Sync.
         */}
        {ohneRolle && (
          <Hinweis art="warnung" className="mb-4" titel="Ihrem Konto ist keine Rolle zugewiesen">
            Ohne die Gruppe <strong>Admin</strong> oder <strong>Erfasser</strong> lädt und sendet
            die App keine Daten — Objekte und Vorlagen bleiben leer, Änderungen bleiben in der
            Warteschlange. Ein Administrator weist die Rolle unter Verwaltung → Benutzer zu.
          </Hinweis>
        )}
        <Outlet />
      </main>
    </div>
  );
};
